import { Geofence, GeofenceType, GeoJsonType, Geometry } from '@eagle/core-data-types';
import { Paper, styled, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { Feature, FeatureCollection } from '@turf/turf';
import { Dispatch, FC, Key, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetchAllCache, usePromise } from '../../../hooks';
import { CacheDataTypes, SetState, Undefinable } from '../../../types';
import ConfirmDialogConfirmation from '../../confirm-dialog/confirm-dialog-confirmation';
import { FloatingButton } from '../../floating-button';
import { Icon } from '../../icons';
import { MapDraw } from '../map-draw';

interface Props {
  'data-testid'?: string;
  clearAddress?: () => void;
  geofenceState: Geofence;
  locked: boolean;
  featureCollection?: FeatureCollection<Geometry>;
  onSave: (feature: Feature<Geometry>) => void;
  onFailure: () => void;
  setLocked: SetState<boolean>;
  setFeatureCollection: Dispatch<SetStateAction<Undefinable<FeatureCollection<Geometry>>>>;
  setShowGeofence: (showGeofence: boolean) => void;
  forceMapRemount?: (val: Key) => void;
  size?: 'small' | 'medium' | 'large';
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
}));

export const LockUnLockControl: FC<Props> = ({
  clearAddress,
  geofenceState,
  locked,
  onFailure,
  onSave,
  setLocked,
  setShowGeofence,
  size = 'small',
  featureCollection,
  setFeatureCollection,
  forceMapRemount,
  ...props
}) => {
  const defaultFeatureCollection: FeatureCollection<Geometry> = {
    features: [{
      geometry: geofenceState.geometry,
      properties: geofenceState.properties,
      type: 'Feature',
    }],
    type: 'FeatureCollection',
  };
  const geofenceTypeCache = useFetchAllCache(CacheDataTypes.GEOFENCE_TYPE);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLockButtonDisable, setIsLockButtonDisabled] = useState(false);
  const { t } = useTranslation(['common']);

  const [geofenceType] = usePromise(
    () => geofenceTypeCache.one<GeofenceType>(geofenceState.geofenceTypeId),
    [geofenceTypeCache, geofenceState],
  );

  const onUnlockButtonClick = (): void => {
    if (!locked && !dialogOpen) return setDialogOpen(true);
    setLocked(false);
    setShowGeofence(false);
  };

  const onCancelButtonClick = (): void => {
    setDialogOpen(false);
    setLocked(true);
    setShowGeofence(true);
    setFeatureCollection(defaultFeatureCollection);
    clearAddress?.();
  };

  const onConfirm = (): Promise<void> => {
    if (!featureCollection?.features.length) {
      onFailure();
      return Promise.resolve();
    }
    const features = featureCollection.features.filter((feature) => feature.geometry.type === GeoJsonType.POLYGON || feature.geometry.type === GeoJsonType.CIRCLE);
    void onSave(features[0]);
    clearAddress?.();
    return Promise.resolve();
  };

  const onSuccess = (): void => {
    setLocked(true);
  };

  const isEditDisabled = dialogOpen;
  const showMapDraw = !locked;

  return (
    <Box data-testid={props['data-testid']} className="leaflet-control" sx={{ display: 'flex', gap: 2 }}>
      {dialogOpen &&
        <StyledPaper elevation={3}>
          <ConfirmDialogConfirmation
            confirmPrompt={<Typography variant="button" pt="2px">{t('common:common.hint.confirm')}</Typography>}
            data-testid="confirm-dialog"
            onConfirm={onConfirm}
            onFailure={onFailure}
            onSuccess={onSuccess}
            setOpen={setDialogOpen}
          />
        </StyledPaper>
      }
      {!dialogOpen && <FloatingButton
        data-testid="lock-button"
        disabled={!featureCollection?.features.length || isLockButtonDisable}
        onClick={onUnlockButtonClick}
        size={size}
        variant="contrast"
      >
        {locked ? <Icon icon="Unlock" style={{ fontSize: '1.25rem' }} /> : <Icon icon="Lock" style={{ fontSize: '1.25rem' }} />}

        <Typography variant="button" ml={1}>
          {locked ? t('common:component.map.labels.unlock-map') : t('common:component.map.labels.save-and-lock')}
        </Typography>
      </FloatingButton>
      }
      {!locked
        && <FloatingButton
          data-testid="cancel-dialog"
          onClick={onCancelButtonClick}
          size={size}
          variant="contrast"
        >
          <Typography variant="button" pt="2px">
            {t('common:common.action.cancel')}
          </Typography>
        </FloatingButton>
      }
      {showMapDraw
        && <MapDraw
          data-testid="map-draw"
          color={geofenceType?.color}
          featureCollection={featureCollection}
          isEditDisabled={isEditDisabled}
          setFeatureCollection={setFeatureCollection}
          forceMapRemount={forceMapRemount}
          setIsLockButtonDisabled={setIsLockButtonDisabled}
        />
      }
    </Box>
  );
};
